import { Controller } from "@hotwired/stimulus"

// Controller identifier: bulk-selection
// Handles:
// - Select all/none/indeterminate states
// - Optional confirmation modal integration
// - Form submission with change detection
// - Works with organizations, agents
export default class extends Controller {
  static targets = ["selectAll", "checkbox", "form", "submitButton", "cancelButton", "pageField", "searchField"]
  static values = {
    confirmRequired: Boolean,
    itemType: String,
    modalId: String
  }

  initialize() {
    this.isReverting = false
    this.showingModal = false
    this.originalStates = new Map()
    this.pendingSubmission = false
    this.agentStates = new Map()
  }

  connect() {
    this.storeOriginalStates()
    this.updateSubmitButtonState()
  }

  storeOriginalStates() {
    this.checkboxTargets.forEach(checkbox => {
      const itemId = this.getItemId(checkbox)
      if (!this.originalStates.has(itemId)) {
        this.originalStates.set(itemId, checkbox.checked)
      }
      if (!this.agentStates.has(itemId)) {
        this.agentStates.set(itemId, checkbox.checked)
      }
      checkbox.dataset.originalState = checkbox.checked.toString()
    })
  }

  restoreCheckboxStates() {
    this.checkboxTargets.forEach(checkbox => {
      const itemId = this.getItemId(checkbox)
      if (this.agentStates.has(itemId)) {
        checkbox.checked = this.agentStates.get(itemId)
      }
    })
    this.updateSelectAllState()
  }

  turboFrameLoad(event) {
    // After turbo frame loads new page, store and restore checkbox states
    this.storeOriginalStates()
    this.restoreCheckboxStates()
    this.updateSubmitButtonState()
  }

  getItemId(checkbox) {
    return checkbox.dataset.resourceId ||
      checkbox.value ||
      checkbox.id
  }

  toggleAll() {
    if (this.isReverting || this.showingModal) return

    const selectAllChecked = this.selectAllTarget.checked

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = selectAllChecked
      var checkboxId = checkbox.dataset.resourceId
      if(checkboxId) {
        this.agentStates.set(checkboxId, selectAllChecked)
      }
    })

    this.updateSelectAllState()
    this.updateSubmitButtonState()
  }

  checkboxChanged(event) {
    if (this.isReverting || this.showingModal) return

    // Store the current state in our persistent map
    const checkbox = event.target
    const itemId = this.getItemId(checkbox)
    this.agentStates.set(itemId, checkbox.checked)

    this.updateSelectAllState()
    this.updateSubmitButtonState()
  }

  updateSelectAllState() {
    const totalCheckboxes = this.checkboxTargets.length
    const checkedCheckboxes = this.checkboxTargets.filter(cb => cb.checked).length

    if (checkedCheckboxes === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checkedCheckboxes === totalCheckboxes) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  hasChanges() {
    let hasChanges = false
    this.agentStates.forEach((currentState, agentId) => {
      const originalState = this.originalStates.get(agentId)
      if (currentState !== originalState) {
        hasChanges = true
      }
    })
    return hasChanges
  }

  // Handle form submission - show modal if confirmation required and changes detected
  handleSubmit(event) {
    if (this.isReverting || this.showingModal || this.pendingSubmission) return

    // Prevent submission if no changes were made
    if (!this.hasChanges()) {
      event.preventDefault()
      return
    }

    if (this.confirmRequiredValue) {
      event.preventDefault()
      this.showConfirmationModal()
    }
  }

  showConfirmationModal() {
    this.showingModal = true

    const enablingCount = this.getEnablingCount()
    const disablingCount = this.getDisablingCount()

    const modalController = this.getModalController()
    if (modalController) {
      modalController.show(enablingCount, disablingCount, { source: "bulk-selection" })
    }
  }

  getModalController() {
    const modalId = this.modalIdValue
    if (modalId) {
      const modalElement = document.getElementById(modalId)
      if (modalElement) {
        return this.application.getControllerForElementAndIdentifier(modalElement, "confirmation-modal")
      }
    }

    // Fallback: look for modal within this element
    const modalElement = this.element.querySelector(".modal")
    if (modalElement) {
      return this.application.getControllerForElementAndIdentifier(modalElement, "confirmation-modal")
    }

    return null
  }

  getEnablingCount() {
    const isOrganization = this.itemTypeValue === "organization"
    let count = 0

    this.agentStates.forEach((currentState, agentId) => {
      const originalState = this.originalStates.get(agentId)
      const changedToEnabled = !originalState && currentState

      if (changedToEnabled) {
        if (isOrganization) {
          const checkbox = this.checkboxTargets.find(cb => this.getItemId(cb) === agentId)
          const impact = checkbox ? parseInt(checkbox.dataset.billingImpact || checkbox.dataset.impactCount || "1") || 0 : 1
          count += impact
        } else {
          count += 1
        }
      }
    })

    return count
  }

  getDisablingCount() {
    const isOrganization = this.itemTypeValue === "organization"
    let count = 0

    // Check ALL agents in state map, not just visible ones
    this.agentStates.forEach((currentState, agentId) => {
      const originalState = this.originalStates.get(agentId)
      const changedToDisabled = originalState && !currentState

      if (changedToDisabled) {
        if (isOrganization) {
          // For organizations, try to find the checkbox to get impact count
          const checkbox = this.checkboxTargets.find(cb => this.getItemId(cb) === agentId)
          const impact = checkbox ? parseInt(checkbox.dataset.billingImpact || checkbox.dataset.impactCount || "1") || 0 : 1
          count += impact
        } else {
          count += 1
        }
      }
    })

    return count
  }

  buildFormDataWithAllAgents() {
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector("form")

    // Remove old hidden bulk_agents inputs (but not the visible checkboxes)
    form.querySelectorAll('input[name^="bulk_agents[agents]"][type="hidden"].bulk-agent-hidden').forEach(input => {
      input.remove()
    })

    // Add hidden inputs for changed agents not on current page
    this.agentStates.forEach((currentState, agentId) => {
      const originalState = this.originalStates.get(agentId)
      const isVisible = this.checkboxTargets.some(cb => this.getItemId(cb) === agentId)

      // If agent changed and is not currently visible, add hidden input
      if (!isVisible && currentState !== originalState) {
        const hiddenInput = document.createElement("input")
        hiddenInput.type = "hidden"
        hiddenInput.name = `bulk_agents[agents][${agentId}]`
        hiddenInput.value = currentState.toString()
        hiddenInput.className = "bulk-agent-hidden"
        form.appendChild(hiddenInput)
      }
    })
  }

  submitForm() {
    this.showingModal = false
    this.pendingSubmission = true

    this.buildFormDataWithAllAgents()

    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector("form")
    form.requestSubmit()
  }

  resetForm() {
    this.revertToOriginalState()
  }

  revertToOriginalState() {
    this.isReverting = true

    // Reset ALL agent states in the map, not just visible ones
    this.agentStates.clear()
    this.originalStates.forEach((originalState, agentId) => {
      this.agentStates.set(agentId, originalState)
    })

    // Reset visible checkboxes
    this.checkboxTargets.forEach(checkbox => {
      const itemId = this.getItemId(checkbox)
      const originalState = this.originalStates.get(itemId)
      checkbox.checked = originalState
    })

    this.updateSelectAllState()

    setTimeout(() => {
      this.isReverting = false
      this.showingModal = false
      this.pendingSubmission = false
      this.updateSubmitButtonState()
    }, 0)
  }

  afterSubmit(event) {
    const { success } = event.detail || {}

    if (success) {
      this.pendingSubmission = false
      this.showingModal = false
      // Reset original states to current states after successful save
      this.storeOriginalStates()
      // Also reset agentStates to match new original states
      this.agentStates.clear()
      this.originalStates.forEach((state, agentId) => {
        this.agentStates.set(agentId, state)
      })
    } else {
      this.revertToOriginalState()
    }
  }
  checkboxTargetConnected(target) {
    const itemId = this.getItemId(target)
    if (!this.originalStates.has(itemId)) {
      this.originalStates.set(itemId, target.checked)
      target.dataset.originalState = target.checked.toString()
    }
    this.updateSelectAllState()
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  updateSubmitButtonState() {
    if (this.hasChanges()) {
      this.enableSubmitButton()
    } else {
      this.disableSubmitButton()
    }
  }
}
